// @ts-strict-ignore
import {AudioDeviceIconType, AudioDeviceInputChannelType, AudioNetworkProtocolType, NetworkAudioInputChannel} from "@models";
import {Component, Injector, OnInit} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import {Translations} from "@translations";

export interface NETWORK_AUDIO_INPUT_CHANNEL_TEMPLATE_INPUT extends MatDialogDataBaseInput {
	audioChannels: NetworkAudioInputChannel[];
}

export interface NETWORK_AUDIO_INPUT_CHANNEL_TEMPLATE_OUTPUT extends MatDialogDataBaseOutput {
	networkAudioInputChannels: NetworkAudioInputChannel[];
}

@Component({
	selector: "sim-network-audio-input-channel-dialog",
	templateUrl: "./network-audio-input-channel-dialog.component.html",
	styleUrls: ["./network-audio-input-channel-dialog.component.scss"],
	standalone: false,
})
export class NetworkAudioInputChannelDialogComponent
	extends BasicMaterialDialog<
		NetworkAudioInputChannelDialogComponent,
		NETWORK_AUDIO_INPUT_CHANNEL_TEMPLATE_INPUT,
		NETWORK_AUDIO_INPUT_CHANNEL_TEMPLATE_OUTPUT
	>
	implements OnInit
{
	T = Translations;
	public audioChannelTypes: {
		name: string;
		value: AudioDeviceInputChannelType;
	}[];
	public audioDeviceIconTypes: {name: string; value: AudioDeviceIconType}[];
	public audioNetworkProtocolTypes: {
		name: string;
		value: AudioNetworkProtocolType;
	}[];

	public amount: number = 1;
	public startChannelNumber: number = null;
	public numberingStyle: "every" | "even" | "odd" = "every";
	public selectedAudioChannelType: AudioDeviceInputChannelType;
	public selectedAudioDeviceIconType: AudioDeviceIconType;
	public seletedAudioNetworkProtocolType: AudioNetworkProtocolType;
	public nameTemplate: string = "Default [Index]";
	public compression: boolean = false;

	constructor(
		private translate: TranslateService,
		_injector: Injector,
	) {
		super(_injector);
	}

	ngOnInit(): void {
		super.ngOnInit();
		this.init();
	}

	protected init() {
		this.initializeNetworkAudioInputChannelType();
		if (this.data.audioChannels) {
			this.startChannelNumber = this.data.audioChannels.length + 1;
		} else {
			this.startChannelNumber = 0;
		}
	}

	ok() {
		const networkAudioInputChannels: any[] = [];

		let multiplicationFactor = 1;

		if (this.numberingStyle === "even" || this.numberingStyle === "odd") {
			multiplicationFactor = 2;
		} else {
			multiplicationFactor = 1;
		}

		for (let i = 0; i < this.amount; i++) {
			const channelOffset = i * multiplicationFactor;

			const networkAudioInputChannel = {
				channel: this.startChannelNumber + channelOffset,
				name: (this.nameTemplate || "Channel [ChannelNumber]")
					.replace("[ChannelNumber]", `${this.startChannelNumber + channelOffset}`)
					.replace("[Index]", `${i + 1}`),
				type: this.selectedAudioChannelType,
				icon: this.selectedAudioDeviceIconType,
				audioNetworkProtocolType: this.seletedAudioNetworkProtocolType,
				compression: this.compression,
			};

			networkAudioInputChannels.push(networkAudioInputChannel);
		}

		this.close({
			action: "ok",
			networkAudioInputChannels: networkAudioInputChannels,
		});
	}

	private initializeNetworkAudioInputChannelType() {
		this.audioChannelTypes = [
			{
				name: "Default Input Channel",
				value: AudioDeviceInputChannelType.Default,
			},
			{name: "Vog Input Channel", value: AudioDeviceInputChannelType.VOG},
			{name: "Vop Input Channel", value: AudioDeviceInputChannelType.VTP},
		];

		this.audioDeviceIconTypes = [
			{name: "VOG", value: AudioDeviceIconType.VOG},
			{name: "Trainer", value: AudioDeviceIconType.Trainer},
			{name: "Microphone", value: AudioDeviceIconType.Microphone},
			{name: "IconXLR", value: AudioDeviceIconType.iconXLR},
			{name: "VTP", value: AudioDeviceIconType.VTP},
			{name: "CAM", value: AudioDeviceIconType.CAM},
			{name: "WirelessMic", value: AudioDeviceIconType.WirelessMic},
			{name: "Telephone", value: AudioDeviceIconType.Telephone},
		];

		this.audioNetworkProtocolTypes = [
			{name: "None", value: AudioNetworkProtocolType.None},
			{name: "Wss", value: AudioNetworkProtocolType.Wss},
			{name: "Tcp", value: AudioNetworkProtocolType.Tcp},
		];

		this.selectedAudioChannelType = this.audioChannelTypes[0].value;
		this.selectedAudioDeviceIconType = this.audioDeviceIconTypes[0].value;
		this.seletedAudioNetworkProtocolType = this.audioNetworkProtocolTypes[0].value;
	}

	checkInputAmount(): string {
		let errorMsg: string = "null";
		if (this.amount === 0 || this.amount === undefined) {
			errorMsg = this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_NUMBER_OF_CHANNELS) + " is empty";
		} else if (this.amount < 1 || this.amount > 100) {
			errorMsg =
				this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_NUMBER_OF_CHANNELS) +
				" must be in the range between 1 and 100";
		}
		return errorMsg;
	}

	checkInputChannelNumber() {
		let errorMsg: string = "null";
		if (this.startChannelNumber === 0 || this.startChannelNumber === undefined) {
			errorMsg = this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_START_CHANNEL_NUMBER) + " is empty";
		} else if (this.startChannelNumber < 1 || this.startChannelNumber > 1000) {
			errorMsg =
				this.translate.instant(this.T.CONFIGURATION_EDITOR_AUDIO_AUDIO_CHANNEL_TEMPLATE_START_CHANNEL_NUMBER) +
				" must be in the range between 1 and 100";
		}
		return errorMsg;
	}
}
